import React, { useState, useEffect } from 'react';
import { useSupabaseClient } from '../utils/supabase';
import { useAuth } from '../utils/AuthProvider';
import { Bell, CheckCircle, Clock, AlertCircle } from 'lucide-react';

interface Notification {
  id: string;
  created_at: string;
  payload: {
    title: string;
    message: string;
    type?: string;
  };
  read_at: string | null;
}

function UserDashboard() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [readCount, setReadCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const supabase = useSupabaseClient();
  const { userDetails } = useAuth();

  useEffect(() => {
    if (userDetails?.id) {
      fetchNotifications();
    }
  }, [userDetails]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      
      if (!userDetails || !userDetails.id) {
        console.log('No user details available');
        setNotifications([]);
        setLoading(false);
        return;
      }
      
      // Fetch notifications targeted at this user
      let { data: userNotifications, error } = await supabase
        .from('webhooks')
        .select('*')
        .filter('target_users', 'cs', `{${userDetails?.user_id}}`)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching user notifications:', error);
        setNotifications([]);
        setLoading(false);
        return;
      }
      
      // Also fetch notifications targeted at any tags this user has
      const { data: userTags, error: tagsError } = await supabase
        .from('users')
        .select('tags')
        .eq('id', userDetails.id)
        .single();
      
      if (tagsError && tagsError.code !== 'PGRST116') {
        console.error('Error fetching user tags:', tagsError);
      }
      
      let tagNotifications: any[] = [];
      
      if (userTags?.tags && Array.isArray(userTags.tags) && userTags.tags.length > 0) {
        const tagNotificationsPromises = userTags.tags.map(tag => 
          supabase
            .from('webhooks')
            .select('*')
            .filter('target_tags', 'cs', `{${tag}}`)
            .order('created_at', { ascending: false })
        );
        
        const tagResults = await Promise.all(tagNotificationsPromises);
        tagNotifications = tagResults
          .filter(result => !result.error)
          .flatMap(result => result.data || []);
      }
      
      // Combine and deduplicate notifications
      const allNotifications = [...(userNotifications || []), ...tagNotifications];
      const uniqueNotifications = Array.from(
        new Map(allNotifications.map(item => [item.id, item])).values()
      );
      
      setNotifications(uniqueNotifications);
      
      // Count read/unread notifications
      setReadCount(uniqueNotifications.filter(n => n.read_at).length);
      setUnreadCount(uniqueNotifications.filter(n => !n.read_at).length);
    } catch (error) {
      console.error('Error in notification fetching:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('webhooks')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId);
      
      if (!error) {
        // Update local state to reflect the change
        setNotifications(prev => 
          prev.map(n => 
            n.id === notificationId 
              ? { ...n, read_at: new Date().toISOString() } 
              : n
          )
        );
        setReadCount(prev => prev + 1);
        setUnreadCount(prev => prev - 1);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Your Notifications
          </h2>
        </div>
      </div>
      
      {/* Stats */}
      <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Bell className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Notifications
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {notifications.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Read Notifications
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {readCount}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Unread Notifications
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {unreadCount}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Notifications List */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Recent Notifications
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Your personalized notification feed.
          </p>
        </div>
        
        {loading ? (
          <div className="px-4 py-8 text-center text-gray-500">
            <Clock className="h-8 w-8 mx-auto animate-spin text-indigo-500 mb-2" />
            <p>Loading notifications...</p>
          </div>
        ) : notifications.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {notifications.map((notification) => (
              <li 
                key={notification.id}
                className={`px-4 py-4 sm:px-6 ${!notification.read_at ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-indigo-600 truncate">
                      {notification.payload.title}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {notification.payload.message}
                    </p>
                    <div className="mt-2 flex">
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                        <p>
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                      
                      {notification.read_at && (
                        <div className="ml-4 flex items-center text-sm text-green-500">
                          <CheckCircle className="flex-shrink-0 mr-1.5 h-4 w-4" />
                          <p>Read</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {!notification.read_at && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="ml-4 px-3 py-1 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none"
                    >
                      Mark as read
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-4 py-8 text-center text-gray-500">
            <Bell className="h-8 w-8 mx-auto text-gray-400 mb-2" />
            <p>No notifications yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserDashboard;